import {
  Button,
  Card,
  DatePicker,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
} from "@tremor/react";
import { ChevronDown, ChevronRight, Filter, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuditLogsInfinite } from "../queries/misc.js";

import { useTranslation } from "react-i18next";

// ─── Action badge colours ────────────────────────────────────────────────────

function actionColor(action) {
  if (!action) return "bg-gray-100 text-gray-600";
  const verb = action.split(".")[1] ?? "";
  if (["deleted", "removed", "cancelled", "revoked"].includes(verb))
    return "bg-red-50 text-red-700";
  if (["created", "invited", "login", "enabled"].includes(verb))
    return "bg-teal-50 text-teal-700";
  if (["updated", "changed", "override_set", "checkout_started"].includes(verb))
    return "bg-amber-50 text-amber-700";
  if (["logout", "disabled", "override_deleted"].includes(verb))
    return "bg-orange-50 text-orange-700";
  return "bg-gray-100 text-gray-600";
}

// ─── Metadata diff viewer ────────────────────────────────────────────────────

function MetaValue({ value, depth = 0 }) {
  if (value === null || value === undefined)
    return <span className="text-gray-400">null</span>;
  if (typeof value === "boolean")
    return <span className="text-purple-600">{String(value)}</span>;
  if (typeof value === "number")
    return <span className="text-blue-600">{value}</span>;
  if (typeof value === "string")
    return <span className="text-green-700">"{value}"</span>;
  if (Array.isArray(value))
    return (
      <span>
        [{value.map((v, i) => (
          <span key={i}>
            {i > 0 && <span className="text-gray-400">, </span>}
            <MetaValue value={v} depth={depth + 1} />
          </span>
        ))}]
      </span>
    );
  if (typeof value === "object") {
    return (
      <span>
        {"{"}
        <div className="ml-4">
          {Object.entries(value).map(([k, v]) => (
            <div key={k}>
              <span className="text-gray-500">{k}: </span>
              <MetaValue value={v} depth={depth + 1} />
            </div>
          ))}
        </div>
        {"}"}
      </span>
    );
  }
  return <span>{String(value)}</span>;
}

function MetadataPanel({ data }) {
  const { t } = useTranslation("audit_log");
  if (!data || Object.keys(data).length === 0)
    return <p className="text-xs text-gray-400">{t("metadata.no_data")}</p>;

  return (
    <div className="space-y-3">
      {Object.entries(data).map(([section, value]) => (
        <div key={section}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {section}
          </p>
          <pre className="overflow-x-auto rounded bg-gray-50 p-3 text-xs font-mono text-gray-700 whitespace-pre-wrap">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}

// ─── Filters bar ─────────────────────────────────────────────────────────────

const RESOURCE_TYPES = [
  "monitor", "incident", "alert_rule", "channel", "user",
  "workspace", "member", "invitation", "api_key", "notification_channel",
  "group", "maintenance", "status_page", "permission", "billing",
];

function FiltersBar({ filters, onChange, onClear }) {
  const { t } = useTranslation("audit_log");
  const [open, setOpen] = useState(false);
  const hasActive = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
            hasActive
              ? "border-teal-300 bg-teal-50 text-teal-700"
              : "border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
        >
          <Filter size={14} />
          {t("filters.title")}
          {hasActive && (
            <span className="ml-1 rounded-full bg-teal-600 px-1.5 py-0.5 text-xs text-white">
              {Object.values(filters).filter(Boolean).length}
            </span>
          )}
        </button>
        {hasActive && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
          >
            <X size={12} /> {t("filters.clear")}
          </button>
        )}
      </div>

      {open && (
        <div className="grid gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t("filters.actor_email")}</label>
            <TextInput
              placeholder="user@example.com"
              value={filters.actor_email ?? ""}
              onValueChange={(v) => onChange("actor_email", v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t("filters.action")}</label>
            <TextInput
              placeholder="monitor.deleted"
              value={filters.action ?? ""}
              onValueChange={(v) => onChange("action", v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t("filters.resource_type")}</label>
            <select
              value={filters.resource_type ?? ""}
              onChange={(e) => onChange("resource_type", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            >
              <option value="">{t("filters.all_types")}</option>
              {RESOURCE_TYPES.map((tkey) => (
                <option key={tkey} value={tkey}>{tkey}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t("filters.from")}</label>
            <DatePicker
              value={filters.date_from ? new Date(filters.date_from) : undefined}
              onValueChange={(date) => onChange("date_from", date ? date.toISOString() : "")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t("filters.to")}</label>
            <DatePicker
              value={filters.date_to ? new Date(filters.date_to) : undefined}
              onValueChange={(date) => onChange("date_to", date ? date.toISOString() : "")}
            />
          </div>
          <div className="flex items-end">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t("filters.done")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function AuditRow({ entry }) {
  const { i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const date = new Date(entry.created_at);
  const dateStr = date.toLocaleDateString(i18n.language, { month: "short", day: "numeric" });
  const timeStr = date.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const hasMetadata = entry.data && Object.keys(entry.data).length > 0;

  return (
    <>
      <TableRow
        className={`cursor-pointer ${hasMetadata ? "hover:bg-gray-50" : ""}`}
        onClick={() => hasMetadata && setExpanded((v) => !v)}
      >
        <TableCell className="w-8 text-gray-300">
          {hasMetadata ? (
            expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : null}
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs text-gray-500">
          <span className="font-mono">{dateStr}</span>{" "}
          <span className="text-gray-400">{timeStr}</span>
        </TableCell>
        <TableCell className="text-sm text-gray-700">
          {entry.actor_email || <span className="text-gray-400">—</span>}
        </TableCell>
        <TableCell>
          <span className={`inline-block rounded px-2 py-0.5 font-mono text-xs ${actionColor(entry.action)}`}>
            {entry.action}
          </span>
        </TableCell>
        <TableCell className="text-xs text-gray-500">
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
            {entry.resource_type}
          </span>
        </TableCell>
        <TableCell className="max-w-xs truncate text-sm text-gray-600">
          {entry.resource_name || entry.resource_id || (
            <span className="text-gray-300">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs text-gray-400">
          {entry.ip_address || "—"}
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-gray-50 px-6 py-4">
            <MetadataPanel data={entry.data} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  actor_email: "",
  action: "",
  resource_type: "",
  date_from: "",
  date_to: "",
};

export default function AuditLog() {
  const { t } = useTranslation(["audit_log", "common"]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Debounced filters drive the actual query (server-side params only)
  const [debouncedFilters, setDebouncedFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(id);
  }, [filters]);

  const queryFilters = useMemo(() => {
    const out = {};
    if (debouncedFilters.action) out.action = debouncedFilters.action;
    if (debouncedFilters.resource_type) out.resource_type = debouncedFilters.resource_type;
    if (debouncedFilters.date_from)
      out.date_from = new Date(debouncedFilters.date_from).toISOString();
    if (debouncedFilters.date_to)
      out.date_to = new Date(debouncedFilters.date_to).toISOString();
    return out;
  }, [debouncedFilters]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useAuditLogsInfinite(queryFilters);

  useEffect(() => {
    if (error) toast.error(error.response?.data?.error ?? t("errors.load_error"));
  }, [error, t]);

  const entries = useMemo(() => {
    const pages = data?.pages ?? [];
    const flat = pages.flatMap((p) => p.data ?? []);
    const emailFilter = debouncedFilters.actor_email?.trim().toLowerCase();
    return emailFilter
      ? flat.filter((e) => e.actor_email?.toLowerCase().includes(emailFilter))
      : flat;
  }, [data, debouncedFilters.actor_email]);

  function handleFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
  }

  function loadMore() {
    if (hasNextPage) fetchNextPage();
  }

  const loading = isLoading;
  const loadingMore = isFetchingNextPage;
  const nextCursor = hasNextPage;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("subtitle")}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <FiltersBar filters={filters} onChange={handleFilterChange} onClear={clearFilters} />
      </div>

      <Card className="p-0">
        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("loading")}</p>
        ) : entries.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">
            {t("empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell className="w-8" />
                  <TableHeaderCell>{t("table.time")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.actor")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.action")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.resource_type")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.resource")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.ip")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {nextCursor && (
          <div className="flex justify-center border-t border-gray-100 px-5 py-4">
            <Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t("common:common.loading") : t("table.load_more")}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
